// src/components/SearchBar.js
import React, { useState, useEffect } from "react";
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  IconButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  Flex,
  Popover,
  PopoverTrigger,
  Stack,
  Checkbox,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  PopoverContent,
  Box,
  Button,
  Select,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaSearch, FaMapMarkerAlt, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import styled from "styled-components";


const SearchBar = ({ onSearch, searchLocation, setSearchLocation }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeFrom, setTimeFrom] = useState(""); // Start Time
  const [timeUntil, setTimeUntil] = useState(""); // End Time
  const [validStartTimes, setValidStartTimes] = useState([]); // Valid Start Times
  const [validEndTimes, setValidEndTimes] = useState([]); // Valid End Times
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const toggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };


  // Theme-aware colors
  const modalBg = useColorModeValue("white", "black");
  const textColor = useColorModeValue("black", "white");
  const circleBg = useColorModeValue("white", "black");
  const checkmarkColor = useColorModeValue("#222", "#f0f0f0");
  const borderColor = useColorModeValue("#a0aec0", "#a0aec0"); // Chakra's gray.500
  
  


  // Reverse geocode coordinates to get an address
  const getAddressFromCoords = async (lat, lng) => {
    try {
      const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
        params: {
          latlng: `${lat},${lng}`,
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        },
      });

      if (response.data.results.length > 0) {
        return response.data.results[0].formatted_address; // Get the actual address
      } else {
        return "Unknown Location";
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      return "Unknown Location";
    }
  };

  // Handle when the GPS button is clicked
  const handleUseCurrentLocation = async () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = { lat: latitude, lng: longitude };
  
          // Fetch the actual address from these fresh coordinates
          const address = await getAddressFromCoords(latitude, longitude);

          // Get the current date & time
          const now = new Date();
          let updatedDate = new Date(now); // Clone current date

          // If it's past 11 PM, move the search to the next day
          if (now.getHours() >= 23) {
            updatedDate.setDate(updatedDate.getDate() + 1);
          }

          // Set default time range: 8:00 AM - 10:00 PM
          const defaultStartTime = "8:00 AM";
          const defaultEndTime = "10:00 PM";

          // Update state with new values
          setSearchQuery(address);
          setSearchLocation(newLocation);
          setSelectedDate(updatedDate);
          console.log(defaultStartTime, defaultEndTime)
          setTimeFrom(defaultStartTime);
          setTimeUntil(defaultEndTime);

          // Automatically fetch photographers after updating location
          onSearch({
            location: newLocation,
            date: updatedDate,
            startTime: defaultStartTime,
            endTime: defaultEndTime,
          });
  
        },
        (error) => {
          console.error("Error fetching current location:", error);
          alert("Could not retrieve your location. Please check location settings.");
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };
  



  // Fetch coordinates for entered address
  const fetchCoordinates = async (address) => {
    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: { address: address, key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY },
        }
      );
  
      if (response.data.results.length > 0) {
        const location = response.data.results[0].geometry.location;
        return { lat: location.lat, lng: location.lng };
      } else {
        return null; // No valid location found
      }
    } catch (error) {
      console.error("Error fetching new location:", error);
      return null;
    }
  };
  


  // Handle Search Button Click
  const handleSearchClick = async () => {
    if (!searchQuery || !selectedDate || !timeFrom || !timeUntil) {
      alert("Please fill in all fields before searching.");
      return;
    }
    let finalLocation = searchLocation; // Default to stored location
    
    // Fetch new coordinates if user typed a new address
    if (searchQuery) {
      const fetchedLocation = await fetchCoordinates(searchQuery);
      console.log(`locationToUse:${searchQuery}` ,finalLocation)
      if (fetchedLocation) {
        finalLocation = fetchedLocation;
        setSearchLocation(fetchedLocation); // Update state for consistency
      } else {
        alert("Could not fetch location for the entered address.");
        return;
      }
    }
  
    console.log("Final Location Used:", finalLocation); // Debugging log
    
    if (!finalLocation || !finalLocation.lat || !finalLocation.lng) {
      alert("Failed to determine location. Please try again.");
      return;
    }
    // Prepare search parameters
    const searchParams = {
      location: finalLocation, // Correct location object
      date: selectedDate,
      startTime: timeFrom,
      endTime: timeUntil,
    };

    onSearch(searchParams); // Send correct data to SearchPage
    onClose(); // Close modal
  };

  // Generate 30-minute time slots from 12:00 AM to 11:30 PM
  const generateTimeSlots = () => {
    const times = [];
    let hour = 0;
    let minute = 0;
    while (hour < 24) {
      const formattedTime = `${hour % 12 === 0 ? 12 : hour % 12}:${minute === 0 ? "00" : minute} ${hour < 12 ? "AM" : "PM"}`;
      times.push(formattedTime);
      minute += 30;
      if (minute === 60) {
        minute = 0;
        hour++;
      }
    }
    return times;
  };
  const timeSlots = generateTimeSlots();

  // Convert time string (e.g., "2:30 PM") to total minutes
  const convertToMinutes = (time) => {
    if (!time) return null;
    const [hourMin, period] = time.split(" ");
    const [hour, minute] = hourMin.split(":").map(Number);
    let totalMinutes = hour * 60 + minute;
    if (period === "PM" && hour !== 12) totalMinutes += 12 * 60;
    if (period === "AM" && hour === 12) totalMinutes -= 12 * 60;
    return totalMinutes;
  };

  // Validate Start Times (show only valid options based on selected date)
  const validateStartTimes = (date) => {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return timeSlots.filter((time) => {
      const timeInMinutes = convertToMinutes(time);
      return isToday ? timeInMinutes > now.getHours() * 60 + now.getMinutes() + 30 : true;
    });
  };

  // Validate End Times (must be at least 1 hour after Start Time)
  const validateEndTimes = (startTime) => {
    const startMinutes = convertToMinutes(startTime);
    return timeSlots.filter((time) => {
      const timeInMinutes = convertToMinutes(time);
      return startMinutes && timeInMinutes > startMinutes && timeInMinutes - startMinutes >= 60;
    });
  };

  // Handle Start Time selection
  const handleTimeFromChange = (e) => {
    const selectedTime = e.target.value;
    setTimeFrom(selectedTime);
    const updatedEndTimes = validateEndTimes(selectedTime);
    setValidEndTimes(updatedEndTimes);
    setTimeUntil(updatedEndTimes.length > 0 ? updatedEndTimes[0] : ""); // Default to first valid end time
  };

  // Handle Search Submission
  const handleSearch = () => {
    if (searchQuery && selectedDate && timeFrom && timeUntil) {
      onSearch({
        location: searchQuery,
        date: selectedDate,
        startTime: timeFrom,
        endTime: timeUntil,
      });
      onClose();
    }
  };

  // Update valid start times when date changes
  useEffect(() => {
    if (selectedDate) {
      setValidStartTimes(validateStartTimes(selectedDate));
      setTimeFrom(""); // Reset time selection when date changes
      setTimeUntil("");
      setValidEndTimes([]);
    }
  }, [selectedDate]);

  return (
    <>
      <Popover placement="bottom-start" closeOnBlur={true}>
  {({ isOpen }) => (
    <>
      {/* Search Bar */}
      <PopoverTrigger>
        <InputGroup borderRadius="md" cursor="pointer">
          <InputLeftElement>
            <IconButton
                aria-label="Use current location"
                icon={<FaMapMarkerAlt />}
                size="sm"
                onClick={handleUseCurrentLocation}
              />
          </InputLeftElement>
          <Input
            placeholder="Search by location..."
            value={searchQuery}
            onClick={onOpen}
            readOnly
          />
          <InputRightElement>

          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>

    </>
  )}
</Popover>

  

      {/* Modal for Location & Time Selection */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent bg={modalBg}>
          <ModalHeader>Select Location & Time</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Location Input */}
              <Box>
                <Text fontWeight="bold">
                   Enter Location
                </Text>
                {/* Location Input with GPS Button */}
                <InputGroup>
                  <Input
                    placeholder="Enter a location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <InputRightElement>
                    {/* GPS Button */}
                    <IconButton
                      aria-label="Use current location"
                      icon={<FaMapMarkerAlt />}
                      size="sm"
                      onClick={handleUseCurrentLocation}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
              
              {/* Date Picker */}
              <Box>
                <Text fontWeight="bold">
                   Select Date
                </Text>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat="MMMM d, yyyy"
                  minDate={new Date()} // Prevent past dates
                  placeholderText="Pick a date"
                  className="date-picker"
                />
              </Box>

              <Flex direction="row" justify="space-between" gap={4} mt={3}>
                {/* Start Time */}
                <Box flex="1">
                  <Text fontWeight="bold" mb={1}>Start Time</Text>
                  <Select
                    value={timeFrom}
                    onChange={handleTimeFromChange}
                    width="100%"
                  >
                    <option value="" disabled>Select a time</option>
                    {validStartTimes.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </Select>
                </Box>

                {/* End Time */}
                <Box flex="1">
                  <Text fontWeight="bold" mb={1}>End Time</Text>
                  <Select
                    value={timeUntil}
                    onChange={(e) => setTimeUntil(e.target.value)}
                    disabled={!validEndTimes.length}
                    width="100%"
                  >
                    <option value="" disabled>Select a time</option>
                    {validEndTimes.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </Select>
                </Box>
              </Flex>

              {/* Search Button */}
              <Button
                colorScheme="purple"
                onClick={() => handleSearchClick(searchQuery, selectedDate, timeFrom, timeUntil)}
                isDisabled={!searchQuery || !selectedDate || !timeFrom || !timeUntil}
              >
                Search
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SearchBar;


